import { 
  BookOpen,
  Brain,
  Shield,
  Code,
  ChartBar,
  Database,
  Users,
  Wallet,
  Building2,
  TrendingUp,
  LayoutGrid,
  Calculator,
  Laptop
} from 'lucide-react';

// Définition des catégories de formation
export const categories = [
  // Catégories techniques
  {
    id: 'fintech',
    name: 'FinTech',
    description: 'Technologies financières innovantes',
    icon: Brain
  },
  {
    id: 'trading',
    name: 'Trading & Marchés',
    description: 'Marchés financiers et trading',
    icon: ChartBar
  },
  {
    id: 'risk',
    name: 'Risque & Conformité',
    description: 'Gestion des risques et conformité',
    icon: Shield
  },
  {
    id: 'dev',
    name: 'Développement',
    description: 'Programmation et architecture',
    icon: Code
  },
  {
    id: 'data',
    name: 'Data & Analytics',
    description: 'Science des données et analyse',
    icon: Database
  },

  // Catégories métiers bancaires
  {
    id: 'retail-banking',
    name: 'Banque de Détail',
    description: 'Services bancaires aux particuliers et professionnels',
    icon: Users
  },
  {
    id: 'private-banking',
    name: 'Banque Privée',
    description: 'Gestion de fortune et ingénierie patrimoniale',
    icon: Wallet
  },
  {
    id: 'corporate-banking',
    name: 'Banque d\'Investissement',
    description: 'Services bancaires aux grandes entreprises',
    icon: Building2
  },
  {
    id: 'capital-markets',
    name: 'Marchés de Capitaux',
    description: 'Trading et opérations de marché',
    icon: TrendingUp
  },
  {
    id: 'operations',
    name: 'Opérations & Services',
    description: 'Back-office et services bancaires',
    icon: LayoutGrid
  },
  {
    id: 'risk-compliance',
    name: 'Risque & Conformité Bancaire',
    description: 'Gestion des risques et conformité réglementaire',
    icon: Shield
  },
  {
    id: 'finance-alm',
    name: 'Finance & ALM',
    description: 'Gestion financière et ALM bancaire',
    icon: Calculator
  },
  {
    id: 'digital-banking',
    name: 'Innovation & Digital Banking',
    description: 'Transformation digitale et innovation bancaire',
    icon: Laptop
  }
];

// Définition des niveaux
export const levels = [
  {
    id: 'beginner',
    name: 'Débutant',
    description: 'Aucun prérequis nécessaire'
  },
  {
    id: 'intermediate',
    name: 'Intermédiaire',
    description: 'Connaissances de base requises'
  },
  {
    id: 'advanced',
    name: 'Avancé',
    description: 'Pour les experts du domaine'
  }
];

// Map des icônes par catégorie
const categoryIcons = {
  'fintech': Brain,
  'trading': ChartBar,
  'risk': Shield,
  'dev': Code,
  'data': Database,
  'retail-banking': Users,
  'private-banking': Wallet,
  'corporate-banking': Building2,
  'capital-markets': TrendingUp,
  'operations': LayoutGrid,
  'risk-compliance': Shield,
  'finance-alm': Calculator,
  'digital-banking': Laptop
};

// État des formations
export let formations = {};

// Import et traitement des formations
const importAllFormations = () => {
  try {
    const context = require.context('./products', true, /\.json$/);
    const loadedFormations = {};
    
    context.keys().forEach((key) => {
      try {
        console.log('Processing file:', key);
        const formationData = context(key);
        const formationId = key.replace(/^.*[\\\/]/, '').replace('.json', '');
        
        loadedFormations[formationId] = {
          ...formationData,
          icon: categoryIcons[formationData.category] || BookOpen
        };
        
        console.log('Successfully loaded formation:', formationId);
      } catch (err) {
        console.error('Error processing file:', key, err);
      }
    });
    
    console.log('All formations loaded:', loadedFormations);
    formations = loadedFormations;
    return loadedFormations;
  } catch (error) {
    console.error('Error importing formations:', error);
    return {};
  }
};

// Charge les formations immédiatement
formations = importAllFormations();

// Export des fonctions utilitaires
export const getFormationsByFilters = (categoryFilter = 'all', levelFilter = 'all') => {
  return Object.values(formations).filter(formation => 
    (categoryFilter === 'all' || formation.category === categoryFilter) &&
    (levelFilter === 'all' || formation.level === levelFilter)
  );
};

export const getRelatedFormations = (formationId, limit = 3) => {
  const currentFormation = formations[formationId];
  if (!currentFormation) return [];
  
  return Object.values(formations)
    .filter(formation => 
      formation.id !== formationId && 
      (formation.category === currentFormation.category || 
       formation.level === currentFormation.level)
    )
    .slice(0, limit);
};

export const getAllFormations = () => Object.values(formations);
export const getFormationById = (id) => formations[id] || null;

export const getFormationsByCategory = (categoryId) => 
  Object.values(formations).filter(formation => formation.category === categoryId);

export const getLatestFormations = (limit = 6) => 
  Object.values(formations)
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
    .slice(0, limit);

export const getFeaturedFormations = (limit = 6) => 
  Object.values(formations)
    .filter(formation => formation.featured)
    .slice(0, limit);

export const getPopularFormations = (limit = 6) => 
  Object.values(formations)
    .sort((a, b) => (b.students || 0) - (a.students || 0))
    .slice(0, limit);

export const getCategoryInfo = (categoryId) => 
  categories.find(category => category.id === categoryId) || null;

export const getLevelInfo = (levelId) =>
  levels.find(level => level.id === levelId) || null;

export const getFormationCountByCategory = () => {
  const counts = {};
  Object.values(formations).forEach(formation => {
    counts[formation.category] = (counts[formation.category] || 0) + 1;
  });
  return counts;
};

export const searchFormations = (query) => {
  const searchTerm = query.toLowerCase();
  return Object.values(formations).filter(formation => 
    formation.title?.toLowerCase().includes(searchTerm) ||
    formation.description?.toLowerCase().includes(searchTerm) ||
    (formation.keywords && formation.keywords.some(keyword => 
      keyword.toLowerCase().includes(searchTerm)
    ))
  );
};

export const filterFormations = ({
  category = 'all',
  level = 'all',
  priceMin,
  priceMax,
  durationMin,
  durationMax,
  searchTerm = ''
}) => {
  return Object.values(formations).filter(formation => {
    const matchesCategory = category === 'all' || formation.category === category;
    const matchesLevel = level === 'all' || formation.level === level;
    const matchesPrice = (!priceMin || formation.price >= priceMin) &&
                        (!priceMax || formation.price <= priceMax);
    const matchesDuration = (!durationMin || formation.duration >= durationMin) &&
                          (!durationMax || formation.duration <= durationMax);
    const matchesSearch = !searchTerm || 
                         formation.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         formation.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesLevel && matchesPrice && matchesDuration && matchesSearch;
  });
};

// Export par défaut
export default {
  categories,
  levels,
  formations,
  getFormationsByFilters,
  getRelatedFormations,
  getAllFormations,
  getFormationById,
  getFormationsByCategory,
  getLatestFormations,
  getFeaturedFormations,
  getPopularFormations,
  getCategoryInfo,
  getLevelInfo,
  getFormationCountByCategory,
  searchFormations,
  filterFormations
};